"use client";

import { CheckIcon, ClipboardIcon } from "@heroicons/react/20/solid";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useCopyToClipboard } from "usehooks-ts";
import { AppError } from "@/errors";
import { Button } from "@/ui/button";
import { pickToasterId, toast } from "@/ui/toast";

type CopyButtonProps = {
  value: string;
  timeoutMs?: number;
  variant?: "default" | "codeOverlay";
  className?: string;
  subject?: string;
};

export function CopyButton({
  value,
  timeoutMs = 3000,
  variant = "default",
  className,
  subject,
}: CopyButtonProps) {
  const t = useTranslations("ui.copyButton");
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | null>(null);
  const [, copy] = useCopyToClipboard();

  async function handleCopy() {
    if (copied) return;
    if (!value) return;
    try {
      const ok = await copy(value);
      if (!ok) throw new AppError("COPY_BUTTON_ERROR", "Failed to copy value");

      setCopied(true);
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => setCopied(false), timeoutMs);
      {
        const toasterId = pickToasterId();
        const title = t("success.title", {
          subject: subject ?? "",
          hasSubject: subject ? "true" : "other",
        });
        toast.success(title, { toasterId });
      }
    } catch {
      const toasterId = pickToasterId();
      const title = t("error.title", {
        subject: subject ?? "",
        hasSubject: subject ? "true" : "other",
      });
      toast.error(title, { toasterId });
    }
  }

  const label = copied ? t("copied") : t("copy");

  const variantClassName =
    variant === "codeOverlay"
      ? [
          "border-ui-fg-on-accent/25",
          "data-hover:!bg-ui-fg-on-accent/10 data-active:!bg-ui-fg-on-accent/10",
          "dark:!border-ui-fg-on-accent/20",
        ].join(" ")
      : undefined;

  return (
    <Button
      outline
      onClick={handleCopy}
      aria-disabled={copied}
      aria-label={label}
      title={label}
      className={twMerge(
        variantClassName,
        className,
        copied ? "cursor-default pointer-events-none" : "cursor-pointer",
      )}
    >
      <AnimatePresence mode="wait" initial={false}>
        {copied ? (
          <motion.span
            key="copied"
            data-slot="icon"
            initial={{ opacity: 0, y: 2, scale: 0.98 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              transition: { duration: 0.14, ease: "easeOut" },
            }}
            exit={{
              opacity: 0,
              y: -2,
              scale: 0.98,
              transition: { duration: 0.1, ease: "easeIn" },
            }}
          >
            <CheckIcon />
          </motion.span>
        ) : (
          <motion.span
            key="copy"
            data-slot="icon"
            initial={{ opacity: 0, y: -2, scale: 0.98 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              transition: { duration: 0.14, ease: "easeOut" },
            }}
            exit={{
              opacity: 0,
              y: 2,
              scale: 0.98,
              transition: { duration: 0.1, ease: "easeIn" },
            }}
          >
            <ClipboardIcon />
          </motion.span>
        )}
      </AnimatePresence>
    </Button>
  );
}
